import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Eye, Sparkles } from 'lucide-react';

const LogoExplanationSection: React.FC = () => {
    const [currentSlide, setCurrentSlide] = useState<number>(0);
    const [isAutoPlaying, setIsAutoPlaying] = useState<boolean>(true);

    const slides = [
        {
            id: 1,
            image: '/logosection/001.webp',
            title: 'Le Palmier aux 10 branches',
            description: 'Symbole d\'élévation représentant les 9 provinces et la diaspora gabonaise'
        },
        {
            id: 2,
            image: '/logosection/002.webp',
            title: 'La Poignée de main',
            description: 'Symbole d\'unité, d\'entente et de solidarité nationale'
        },
        {
            id: 3,
            image: '/logosection/003.webp',
            title: 'L\'Ouvrier à la brouette',
            description: 'Le bâtisseur qui œuvre pour l\'édification de la nation'
        },
        {
            id: 4,
            image: '/logosection/004.webp',
            title: 'La Carte du Gabon',
            description: 'Notre belle nation qui encadre nos valeurs communes'
        },
        {
            id: 5,
            image: '/logosection/005.webp',
            title: 'Les Valeurs UDB',
            description: 'Inclusivité, Développement et Félicité - Nos trois piliers fondamentaux'
        }
    ];

    // Auto-play functionality
    useEffect(() => {
        if (!isAutoPlaying) return;

        const interval = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % slides.length);
        }, 5000);

        return () => clearInterval(interval);
    }, [isAutoPlaying, slides.length]);

    const goToSlide = (index: number) => {
        setCurrentSlide(index);
        setIsAutoPlaying(false);
    };

    const goToPrevious = () => {
        setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
        setIsAutoPlaying(false);
    };

    const goToNext = () => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
        setIsAutoPlaying(false);
    };

    return (
        <section className="py-16 lg:py-20 bg-gradient-to-br from-slate-50 via-white to-slate-50">
            <div className="container mx-auto px-4">
                {/* Header moderne */}
                <div className="text-center mb-12 lg:mb-16">
                    <div className="flex items-center justify-center space-x-3 mb-6">
                        <div className="p-2 bg-primary-blue/10">
                            <Eye className="w-6 h-6 text-primary-blue" />
                        </div>
                        <span className="text-primary-blue text-sm font-semibold uppercase tracking-wider bg-primary-blue/10 px-3 py-1">
                            Notre identité
                        </span>
                    </div>
                    <h2 className="text-3xl lg:text-4xl font-light text-gray-900 leading-tight mb-4">
                        La symbolique
                        <span className="block text-primary-green">de notre logo</span>
                    </h2>
                    <div className="w-16 h-1 bg-primary-yellow mx-auto"></div>
                    <p className="text-lg text-gray-600 mt-6 max-w-2xl mx-auto">
                        Chaque élément raconte l'histoire du Gabon et porte nos valeurs les plus profondes
                    </p>
                </div>

                {/* Slider Container */}
                <div className="max-w-4xl mx-auto">
                    <div className="relative bg-white shadow-lg overflow-hidden">
                        {/* Slider Images */}
                        <div className="relative h-64 sm:h-80 lg:h-[500px]">
                            {slides.map((slide, index) => (
                                <div
                                    key={slide.id}
                                    className={`absolute inset-0 transition-opacity duration-500 ${
                                        index === currentSlide ? 'opacity-100' : 'opacity-0'
                                    }`}
                                >
                                    <img
                                        src={slide.image}
                                        alt={slide.title}
                                        className="w-full h-full object-contain bg-gray-50"
                                    />
                                </div>
                            ))}

                            {/* Navigation Arrows */}
                            <button
                                onClick={goToPrevious}
                                className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white text-gray-800 p-2 transition-all duration-300 z-10"
                            >
                                <ChevronLeft className="w-5 h-5" />
                            </button>
                            <button
                                onClick={goToNext}
                                className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white text-gray-800 p-2 transition-all duration-300 z-10"
                            >
                                <ChevronRight className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Slide Content */}
                        <div className="p-6 lg:p-8">
                            <div className="text-center">
                                <h3 className="text-2xl lg:text-3xl font-light text-gray-900 mb-4">
                                    {slides[currentSlide].title}
                                </h3>
                                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                                    {slides[currentSlide].description}
                                </p>
                            </div>
                        </div>

                        {/* Dots Navigation */}
                        <div className="pb-6">
                            <div className="flex justify-center space-x-2">
                                {slides.map((_, index) => (
                                    <button
                                        key={index}
                                        onClick={() => goToSlide(index)}
                                        className={`w-3 h-3 transition-all duration-300 ${
                                            index === currentSlide
                                                ? 'bg-primary-blue'
                                                : 'bg-gray-300 hover:bg-gray-400'
                                        }`}
                                    />
                                ))}
                            </div>
                        </div>

                        {/* Progress Bar */}
                        <div className="absolute bottom-0 left-0 w-full h-1 bg-gray-200">
                            <div
                                className="h-full bg-primary-blue transition-all duration-300"
                                style={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
                            ></div>
                        </div>
                    </div>

                    {/* Auto-play control */}
                    <div className="text-center mt-6">
                        <button
                            onClick={() => setIsAutoPlaying(!isAutoPlaying)}
                            className={`text-sm px-4 py-2 transition-all duration-300 ${
                                isAutoPlaying
                                    ? 'bg-primary-blue text-white'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                        >
                            {isAutoPlaying ? 'Pause' : 'Lecture automatique'}
                        </button>
                    </div>
                </div>

                {/* Call to Action */}
                <div className="mt-16 lg:mt-20">
                    <div className="relative overflow-hidden">
                        <div className="relative bg-primary-blue p-8 lg:p-12 text-white text-center">
                            <div className="max-w-4xl mx-auto">
                                <div className="flex items-center justify-center space-x-3 mb-6">
                                    <Sparkles className="w-8 h-8 text-primary-yellow" />
                                    <h3 className="text-2xl lg:text-3xl font-light">
                                        Un logo, une vision, un engagement
                                    </h3>
                                    <Sparkles className="w-8 h-8 text-primary-yellow" />
                                </div>

                                <div className="w-16 h-1 bg-primary-yellow mx-auto mb-6"></div>

                                <p className="text-lg leading-relaxed mb-8 max-w-2xl mx-auto">
                                    Chaque trait, chaque couleur, chaque symbole porte notre promesse d'un Gabon <strong>uni</strong>,
                                    <strong> développé</strong> et <strong>heureux</strong>. Rejoignez-nous dans cette construction collective.
                                </p>

                                <button
                                    onClick={() => document.getElementById('adhesion')?.scrollIntoView({ behavior: 'smooth' })}
                                    className="bg-white text-primary-blue px-8 py-3 font-semibold hover:bg-primary-yellow hover:text-gray-900 transition-all duration-300 inline-flex items-center space-x-2 group"
                                >
                                    <span>REJOINDRE LE MOUVEMENT</span>
                                    <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default LogoExplanationSection;
